/** Each league's label for a day, its record against break-even and its comparison lines. */

import { getLeagueLabel } from '@parlay-picker/shared/league';
import { type DirectionResult, marketConditionLabel } from '@parlay-picker/shared/marketCondition';
import { comparisonLines, type LeagueMarketCondition } from '@parlay-picker/shared/marketDayLabels';
import type { ReactElement } from 'react';
import { getCallColor, getConditionColor } from './marketInsightsHelpers';

function DirectionRecord({
  name,
  result,
}: {
  name: string;
  result: DirectionResult;
}): ReactElement {
  if (result.picks === 0 || result.breakeven === null) {
    return <span className="text-theme-text-tertiary">No {name.toLowerCase()}</span>;
  }
  const hitRate = Math.round((result.hits / result.picks) * 100);
  return (
    <span>
      {name}{' '}
      <span className={getCallColor(result.call)}>
        {result.hits}/{result.picks} ({hitRate}%)
      </span>{' '}
      <span className="text-theme-text-tertiary">
        vs {Math.round(result.breakeven * 100)}% break-even
      </span>
      {result.reason === 'too_few_picks' && (
        <span className="text-theme-text-tertiary"> · too few picks</span>
      )}
    </span>
  );
}

export function LeagueConditionsList({
  leagues,
}: {
  leagues: LeagueMarketCondition[];
}): ReactElement | null {
  if (leagues.length === 0) return null;
  return (
    <div className="bg-theme-surface/50 rounded-lg p-3 space-y-3" data-testid="league-conditions">
      <h3 className="text-sm font-semibold text-theme-text">By League</h3>
      {leagues.map((league) => (
        <div key={league.leagueKey} className="text-xs space-y-1">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium text-theme-text">{getLeagueLabel(league.leagueKey)}</span>
            <span className={getConditionColor(league.condition)}>
              {marketConditionLabel(league.condition)}
            </span>
          </div>
          <div className="flex flex-col gap-0.5 text-theme-text-secondary">
            <DirectionRecord name="Overs" result={league.over} />
            <DirectionRecord name="Unders" result={league.under} />
          </div>
          {comparisonLines(league).map((line) => (
            <p key={line} className="text-theme-text-tertiary italic">
              {line}
            </p>
          ))}
        </div>
      ))}
    </div>
  );
}
